import type { JSX } from "react";

import type { CityLiveCostOfLiving } from "@noko-city/shared";
import { humaniseCostGroup } from "@noko-city/shared";

import { Reveal } from "./Reveal";

/**
 * The guide's price panels.
 *
 * A reading the backend derives from its own tables and bakes into the page, so
 * it is not authored copy and the browser never fetches it.
 */

/** The restaurant rows the guide prints, in its own order: presentation only. */
const GUIDE_RESTAURANT_KEYS: readonly string[] = [
  "meal-for-two-at-a-mid-range-restaurant",
  "domestic-draft-beer",
  "cappuccino",
];

/**
 * Our own names and one icon each, ours rather than the publisher's; an
 * unnamed key still prints the stored label.
 */
const GUIDE_COST_ITEMS: Record<string, { label: string; icon: string }> = {
  "meal-for-two-at-a-mid-range-restaurant": { label: "Dinner for two", icon: "🥂" },
  "domestic-draft-beer": { label: "Draft beer 0.5l", icon: "🍺" },
  cappuccino: { label: "Cappuccino", icon: "☕" },
  "domestic-beer": { label: "Beer 0.5l bottle", icon: "🍺" },
  "bottle-of-wine": { label: "Bottle of wine", icon: "🍷" },
  cigarettes: { label: "Cigarettes, pack of 20", icon: "🚬" },
  "one-way-ticket": { label: "One-way ticket", icon: "🎫" },
  "taxi-start": { label: "Taxi flagfall", icon: "🚕" },
  "taxi-1-km": { label: "Taxi, 1 km", icon: "🛣️" },
};

const COST_AMOUNT_FORMAT = new Intl.NumberFormat("en-GB", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  useGrouping: false,
});

function formatCost(amount: number, currency: string): string {
  if (!Number.isFinite(amount)) return "unavailable";
  return `${COST_AMOUNT_FORMAT.format(amount)}${currency}`;
}

interface CostPanel {
  group: string;
  items: CityLiveCostOfLiving["items"];
}

export function GuideCostTable({ cost }: { cost: CityLiveCostOfLiving }): JSX.Element {
  const panels: CostPanel[] = [];
  for (const item of cost.items) {
    if (item.itemGroup === "restaurants" && !GUIDE_RESTAURANT_KEYS.includes(item.key)) continue;
    const bucket = panels.find((candidate) => candidate.group === item.itemGroup);
    if (bucket) bucket.items.push(item);
    else panels.push({ group: item.itemGroup, items: [item] });
  }
  for (const { group, items } of panels) {
    if (group !== "restaurants") continue;
    items.sort(
      (left, right) =>
        GUIDE_RESTAURANT_KEYS.indexOf(left.key) - GUIDE_RESTAURANT_KEYS.indexOf(right.key),
    );
  }
  return (
    <div className="atlas-guide-cost">
      <Reveal variant="rise">
        <div className="atlas-guide-cost-grid">
          {panels.map(({ group, items }, position) => {
            const title = humaniseCostGroup(group);
            return (
              <section key={group} className="atlas-guide-cost-panel" aria-label={title}>
                <div className="atlas-guide-cost-heading">
                  <span className="atlas-guide-cost-index" aria-hidden="true">
                    {String(position + 1).padStart(2, "0")}
                  </span>
                  <h3 className="atlas-guide-cost-title">{title}</h3>
                </div>
                <table className="atlas-guide-cost-table" aria-label={`${title} prices`}>
                  <tbody>
                    {items.map((item) => {
                      const guide = GUIDE_COST_ITEMS[item.key];
                      return (
                        <tr
                          key={item.key}
                          className="atlas-guide-cost-row"
                          data-cost-key={item.key}
                        >
                          <th scope="row" className="atlas-guide-cost-cell">
                            <span className="atlas-guide-cost-item">
                              <span className="atlas-guide-cost-icon" aria-hidden="true">
                                {guide?.icon ?? ""}
                              </span>
                              {guide?.label ?? item.label}
                            </span>
                          </th>
                          <td className="atlas-guide-cost-cell">
                            <span className="atlas-guide-cost-figure">
                              {formatCost(item.priceLocal, cost.currency)}
                            </span>
                          </td>
                          <td className="atlas-guide-cost-cell">
                            <span className="atlas-guide-cost-figure atlas-guide-cost-eur">
                              {item.priceEur === null || !Number.isFinite(item.priceEur) ? (
                                "unavailable"
                              ) : (
                                <>
                                  <span className="sr-only">Approximate </span>
                                  {`~ ${formatCost(item.priceEur, "EUR")}`}
                                </>
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            );
          })}
        </div>
      </Reveal>
    </div>
  );
}
